/**
 * How map characters look in the kit: the game's own sprites (via `cellLook`), with a few editor
 * touches the game does not need: the inbox shows Digby, the exit shows its door, creatures carry
 * a tiny arrow for the way they set off, and `?` cells show a question mark.
 */

import { LEGEND } from '../../caves/legend';
import type { Atlas } from '../../render/atlas';
import { cellLook } from '../../render/cellSprite';
import { BLACK, GREY, RED, WHITE } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { SpriteName } from '../../render/sprites';
import type { CaveState } from '../../sim/types';

// Enough of a cave state for `cellLook`: an active magic wall, so it shimmers and is told from brick.
const STILL = {
  w: 1,
  h: 1,
  cells: new Uint8Array(1),
  digby: { x: 0, y: 0, born: false, alive: true, facing: 1, moving: false, pushing: false, idle: 0 },
  magic: { phase: 'active', msLeft: 0 },
  scan: 0,
} as unknown as CaveState;

export interface KitLook {
  readonly sprite: SpriteName;
  readonly frame: number;
}

/** The sprite for a map character, or null (space, `?`, unknown characters). */
export function kitLook(ch: string, anim: number): KitLook | null {
  if (ch === 'P') return { sprite: 'digbyStand', frame: 0 };
  if (ch === 'X') return { sprite: 'door', frame: 0 };
  const el = LEGEND[ch];
  if (el === undefined) return null;
  const look = cellLook(el, STILL, anim);
  return look.sprite ? { sprite: look.sprite, frame: look.frame } : null;
}

/** Draw the character's sprite at 16 x 16 in the context's current transform. */
export function drawSprite(
  ctx: CanvasRenderingContext2D,
  atlas: Atlas,
  ch: string,
  x: number,
  y: number,
  anim: number,
): void {
  const look = kitLook(ch, anim);
  if (look) atlas.draw(ctx, look.sprite, look.frame, x, y);
}

type Heading = 'left' | 'right' | 'up' | 'down';

const HEADINGS: Readonly<Record<string, Heading>> = { f: 'left', F: 'right', b: 'down', B: 'up' };

/** A tiny white triangle on a black backing: `n` rows deep, its tip pointing `h`. */
function triangle(s: Screen, h: Heading, x: number, y: number, n: number): void {
  const across = 2 * n - 1;
  const horizontal = h === 'left' || h === 'right';
  s.fill(x - 1, y - 1, (horizontal ? n : across) + 2, (horizontal ? across : n) + 2, BLACK);
  for (let i = 0; i < n; i++) {
    const k = h === 'left' || h === 'up' ? i : n - 1 - i;
    if (horizontal) s.fill(x + k, y + n - 1 - i, 1, 2 * i + 1, WHITE);
    else s.fill(x + n - 1 - i, y + k, 2 * i + 1, 1, WHITE);
  }
}

/**
 * The editor's marks over a cell drawn at screen (x, y), `size` pixels square: creature headings,
 * `?` for random fill, and a red warning for a character the legend does not know.
 */
export function drawMark(s: Screen, ch: string, x: number, y: number, size: number): void {
  const heading = HEADINGS[ch];
  if (heading) {
    const n = size >= 16 ? 3 : 2;
    const horizontal = heading === 'left' || heading === 'right';
    const w = horizontal ? n : 2 * n - 1;
    const h = horizontal ? 2 * n - 1 : n;
    return triangle(s, heading, x + size - w - 1, y + size - h - 1, n);
  }
  if (ch === '?') return s.text('?', x + size / 2 - 4, y + size / 2 - 4, GREY);
  if (LEGEND[ch] === undefined && ch !== ' ') {
    s.fill(x, y, size, size, RED);
    s.text('!', x + size / 2 - 4, y + size / 2 - 4, WHITE);
  }
}

/** Sprite and marks for one cell at screen (x, y), 16 x 16 (palette, fill slots). */
export function drawCellAt(s: Screen, atlas: Atlas, ch: string, x: number, y: number, anim: number): void {
  drawSprite(s.ctx, atlas, ch, x, y, anim);
  drawMark(s, ch, x, y, 16);
}
